import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { ModuleCarouselFragment } from './__generated__/fragment'
import { Module, ModuleContext } from '../types'
import {
  CarouselControls,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
  CarouselRoot,
  CarouselScrollArea,
  CarouselState,
  useCarousel,
  carouselShallowCompare,
} from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { v4 as uuidv4 } from 'uuid'

import { ModuleSection } from '../../components/ModuleContainer'
import { HeroModule } from '../Hero'
import { useFindLocaleText } from '../../text-localisation'

const AUTO_SLIDE_INTERVAL_MS = 7000

const StyledCarouselRoot = styled(CarouselRoot)`
  ${({ theme: { siteWidth } }) =>
    s({
      maxWidth: siteWidth,
      mx: 'auto',
      gap: 0,
    })}
  padding-left: 0;
  padding-right: 0;
`

const responsiveTopCarouselControls = s({
  top: {
    xs: '-50%',
    md: 0,
  },
})

const StyledCarouselPreviousButton = styled(CarouselPreviousButton)`
  left: ${({ theme }) => theme.spacing[6]}px;
  z-index: 5;
  ${responsiveTopCarouselControls}
`

const StyledCarouselNextButton = styled(CarouselNextButton)`
  right: ${({ theme }) => theme.spacing[6]}px;
  z-index: 5;
  ${responsiveTopCarouselControls}
`

const StyledCarouselItem = styled(CarouselItem)`
  width: 100%;
`

const StyledCarouselIndicatorWrapper = styled('div')`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing[4]}px;
`

const StyledCarouselIndicator = styled('button')<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;

  &::after {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    transition: background-color 300ms ease;
    background-color: ${({ theme, isActive }) =>
      isActive ? theme.palette.colorPrimary02 : theme.palette.colorBlack20};
  }
`

type ModuleHeroCarouselItem = Extract<
  ModuleCarouselFragment['items'][number],
  { __typename: 'ModuleHero' }
>

const getModuleHeroItems = (
  items: ModuleCarouselFragment['items'],
): ModuleHeroCarouselItem[] =>
  items.filter(
    (item): item is ModuleHeroCarouselItem => item.__typename === 'ModuleHero',
  )

const buildCarouselTrackingLabel = (
  items: ModuleHeroCarouselItem[],
  position: number,
) => {
  const currentItemTitle = items[position - 1]?.title
  return `${currentItemTitle} | carousel | ${position}/${items.length}`
}

type ModuleCarouselItem = ModuleHeroCarouselItem

type CarouselContentProps = {
  items: ModuleCarouselItem[]
  context: ModuleContext
}

const CarouselContent: FC<CarouselContentProps> = ({ items, context }) => {
  const { tracking } = context
  const previousIndexRef = useRef(0)
  const directionRef = useRef<1 | -1>(1)

  const {
    currentIndex: currentCarouselIndex,
    gotoItemIndex: goToCarouselItemIndex,
  } = useCarousel(
    ({ currentIndex, gotoItemIndex }) => ({
      currentIndex,
      gotoItemIndex,
    }),
    carouselShallowCompare,
  )

  const [visualIndicatorIndex, setVisualIndicatorIndex] = useState(0)

  const handlePreviousClick = () => {
    setVisualIndicatorIndex(currentCarouselIndex - 1)
    goToCarouselItemIndex(currentCarouselIndex - 1)
    tracking.selectPromotion({
      module: 'carousel',
      label: buildCarouselTrackingLabel(items, previousIndexRef.current + 1),
      action: 'previous',
    })
  }

  const handleNextClick = () => {
    setVisualIndicatorIndex(currentCarouselIndex + 1)
    goToCarouselItemIndex(currentCarouselIndex + 1)
    tracking.selectPromotion({
      module: 'carousel',
      label: buildCarouselTrackingLabel(items, previousIndexRef.current + 1),
      action: 'next',
    })
  }

  const handleScroll = useCallback(
    ({ currentIndex }: { currentIndex: number }) => {
      if (currentIndex !== previousIndexRef.current) {
        previousIndexRef.current = currentIndex
      }
    },
    [],
  )

  const goToItem = (index: number) => {
    setVisualIndicatorIndex(index)
    goToCarouselItemIndex(index)
  }

  const lastIndex = items.length - 1

  useEffect(() => {
    if (items.length <= 1) return undefined

    const timeoutId = window.setTimeout(() => {
      if (currentCarouselIndex >= lastIndex) {
        directionRef.current = -1
      }

      if (currentCarouselIndex <= 0) {
        directionRef.current = 1
      }

      const nextIndex = currentCarouselIndex + directionRef.current
      setVisualIndicatorIndex(nextIndex)
      goToCarouselItemIndex(nextIndex)
    }, AUTO_SLIDE_INTERVAL_MS)

    return () => window.clearTimeout(timeoutId)
  }, [
    currentCarouselIndex,
    goToCarouselItemIndex,
    items.length,
    setVisualIndicatorIndex,
    lastIndex,
  ])

  const localiseText = useFindLocaleText()
  const indicatorText = localiseText('find.carouselIndicator')

  return (
    <StyledCarouselRoot aria-label={localiseText('find.carousel')}>
      <CarouselControls>
        <StyledCarouselPreviousButton onClick={handlePreviousClick} />
        <StyledCarouselNextButton onClick={handleNextClick} />
      </CarouselControls>
      <CarouselScrollArea scrollBy={1} onScroll={handleScroll}>
        {items.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <StyledCarouselItem key={`carousel-item-${index}`}>
            <HeroModule
              module={item}
              context={context}
              onCtaSelect={() => {
                tracking.selectPromotion({
                  module: 'carousel',
                  label: buildCarouselTrackingLabel(items, index + 1),
                  action: 'select',
                })
              }}
            />
          </StyledCarouselItem>
        ))}
      </CarouselScrollArea>
      {items.length > 1 && (
        <StyledCarouselIndicatorWrapper>
          {items.map((item, index) => (
            <StyledCarouselIndicator
              isActive={index === visualIndicatorIndex}
              type="button"
              key={`${item.title}-module-indicator ${uuidv4()}`}
              aria-label={`${indicatorText} ${index + 1} - ${index === visualIndicatorIndex ? 'active' : 'inactive'} `}
              onClick={() => goToItem(index)}
            />
          ))}
        </StyledCarouselIndicatorWrapper>
      )}
    </StyledCarouselRoot>
  )
}

export const CarouselModule: FC<Module<ModuleCarouselFragment>> = ({
  module,
  context,
}) => {
  const { tracking } = context
  const heroItems = getModuleHeroItems(module.items)

  const [inViewRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  })

  useEffect(() => {
    if (!inView || heroItems.length === 0) return

    tracking.viewPromotion({
      module: 'carousel',
      label: buildCarouselTrackingLabel(heroItems, 1),
    })
  }, [inView, heroItems, tracking])

  if (heroItems.length === 0) {
    return null
  }

  return (
    <ModuleSection data-testid="module-carousel" ref={inViewRef}>
      <CarouselState>
        <CarouselContent items={heroItems} context={context} />
      </CarouselState>
    </ModuleSection>
  )
}
